import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Fade,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import { useSettings } from "../../context/SettingsContext";
import { motion, AnimatePresence } from "framer-motion";

const MotionBox = motion.create ? motion.create(Box) : motion(Box);
const MotionChip = motion.create ? motion.create(Chip) : motion(Chip);

// Service color palette for variety
const serviceColors = [
  { bg: "#F3E8FF", color: "#7C3AED", hoverBg: "#EDE9FE" }, // Purple
  { bg: "#E0F2FE", color: "#0891B2", hoverBg: "#BAE6FD" }, // Cyan
  { bg: "#FCE7F3", color: "#DB2777", hoverBg: "#FBCFE8" }, // Pink
  { bg: "#D1FAE5", color: "#059669", hoverBg: "#A7F3D0" }, // Green
  { bg: "#FEF3C7", color: "#D97706", hoverBg: "#FDE68A" }, // Amber
  { bg: "#E0E7FF", color: "#4F46E5", hoverBg: "#C7D2FE" }, // Indigo
];

const getServiceColor = (index) => {
  return serviceColors[index % serviceColors.length];
};

export default function ServicesBlock({ lead, updateLead }) {
  const theme = useTheme();
  const { services } = useSettings();
  const [selected, setSelected] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const svc = lead?.services || [];
    const cfSvc = Array.isArray(lead?.custom_fields?.services_interested)
      ? lead.custom_fields.services_interested
      : [];
    const all = [...new Set([...svc, ...cfSvc].filter(Boolean))];
    // Only keep services that exist in Settings (case-insensitive)
    const allowedLower = (services || []).map((s) => s.toLowerCase());
    const filtered = all.filter((s) => allowedLower.includes(s.toLowerCase()));
    setSelected(filtered);
  }, [lead?._id, services]);

  const handleAddClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleServiceToggle = (service) => {
    const newSelected = selected.includes(service)
      ? selected.filter((s) => s !== service)
      : [...selected, service];

    setSelected(newSelected);
    updateLead("services", newSelected);
  };

  const handleRemoveService = (service) => {
    const newSelected = selected.filter((s) => s !== service);
    setSelected(newSelected);
    updateLead("services", newSelected);
  };

  // Get available services that are not yet selected
  const availableServices = (services || []).filter(
    (service) => !selected.includes(service)
  );

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      sx={{
        borderRadius: 4,
        bgcolor: "#FFFFFF",
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        border: "1px solid #F1F5F9",
        overflow: "hidden",
        p: 3,
      }}
    >
      {/* Header */}
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 600,
          color: "#1F2937",
          mb: 2.5,
          letterSpacing: "-0.01em",
        }}
      >
        Services Interested
      </Typography>

      {/* Services Chips */}
      <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
        <AnimatePresence mode="popLayout">
          {selected.map((service, index) => {
            const colors = getServiceColor(index);
            return (
              <MotionChip
                key={service}
                label={service}
                onDelete={() => handleRemoveService(service)}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                whileHover={{ scale: 1.02 }}
                sx={{
                  height: 40,
                  px: 0.5,
                  bgcolor: colors.bg,
                  color: colors.color,
                  fontWeight: 600,
                  fontSize: 14,
                  borderRadius: "20px",
                  border: "none",
                  transition: "all 0.2s ease",
                  "& .MuiChip-label": {
                    px: 2,
                    py: 1,
                    textTransform: "capitalize",
                  },
                  "& .MuiChip-deleteIcon": {
                    color: colors.color,
                    opacity: 0.6,
                    fontSize: 18,
                    mr: 0.5,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: colors.color,
                      opacity: 1,
                    },
                  },
                  "&:hover": {
                    bgcolor: colors.hoverBg,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  },
                }}
              />
            );
          })}
        </AnimatePresence>

        {/* Add Button + Empty State */}
        <MotionBox
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
        >
          <IconButton
            onClick={handleAddClick}
            sx={{
              width: 40,
              height: 40,
              border: "2px dashed #D1D5DB",
              borderRadius: "50%",
              color: "#9CA3AF",
              bgcolor: "transparent",
              transition: "all 0.25s ease",
              "&:hover": {
                borderColor: "primary.main",
                color: "primary.main",
                bgcolor: "rgba(124, 58, 237, 0.04)",
                transform: "rotate(90deg)",
              },
            }}
          >
            <AddIcon sx={{ fontSize: 20 }} />
          </IconButton>
          {selected.length === 0 && (
            <Typography
              sx={{
                fontSize: 14,
                color: "#9CA3AF",
                fontStyle: "italic",
              }}
            >
              No services selected
            </Typography>
          )}
        </MotionBox>

        {/* Dropdown Menu */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          TransitionComponent={Fade}
          transitionDuration={200}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 3,
              boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
              border: "1px solid #F1F5F9",
              minWidth: 220,
              maxHeight: 300,
              overflow: "auto",
              bgcolor: "#fff",
              "& .MuiList-root": {
                py: 1,
              },
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#fff",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: theme.palette.primary.main,
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
              scrollbarWidth: "thin",
              scrollbarColor: `${theme.palette.primary.main} #fff`,
            },
          }}
        >
          {availableServices.length > 0 ? (
            availableServices.map((service, index) => {
              const colors = getServiceColor(index);
              return (
                <MenuItem
                  key={service}
                  onClick={() => handleServiceToggle(service)}
                  sx={{
                    py: 1.25,
                    px: 2,
                    mx: 1,
                    my: 0.5,
                    borderRadius: 2,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: colors.bg,
                    },
                  }}
                >
                  <Checkbox
                    checked={selected.includes(service)}
                    sx={{
                      p: 0,
                      mr: 1.5,
                      color: "#D1D5DB",
                      "&.Mui-checked": {
                        color: "#7C3AED",
                      },
                    }}
                    size="small"
                  />
                  <ListItemText
                    primary={service}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#374151",
                      textTransform: "capitalize",
                    }}
                  />
                </MenuItem>
              );
            })
          ) : (
            <MenuItem disabled sx={{ py: 2, justifyContent: "center" }}>
              <Typography
                sx={{
                  fontSize: 13,
                  color: "#9CA3AF",
                  fontStyle: "italic",
                }}
              >
                All services selected
              </Typography>
            </MenuItem>
          )}
        </Menu>
      </Stack>

    </MotionBox>
  );
}
